"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Loader2, CheckCircle2, AlertCircle, Leaf } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-primary/10 flex items-center justify-center">
          <CheckCircle2 className="size-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">¡Inscripción Recibida!</h3>
          <p className="text-muted-foreground max-w-sm mx-auto">{successMessage}</p>
        </div>
        <Button asChild variant="outline" className="mt-4 border-primary/30 hover:bg-primary/5">
          <Link to="https://wa.me/59896344395" newTab>
            Contactar por WhatsApp
          </Link>
        </Button>
      </div>;
  }
  const inputStyles = "border-primary/30 focus-visible:border-primary focus-visible:ring-primary/20 bg-background/50 backdrop-blur-sm";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-1 mb-6">
        <h3 className="text-2xl font-bold text-foreground">Invitación a Inscribirse</h3>
        <p className="text-sm text-muted-foreground font-medium text-primary">Supervisor Forestal</p>
      </div>

      <FormField control={control} name="full_name" rules={{
      required: "El nombre completo es requerido"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Nombre completo</FormLabel>
            <FormControl>
              <Input placeholder="Juan Pérez" className={inputStyles} {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/80">
              Ingrese su nombre y apellido tal como aparecen en su documento de identidad.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "El correo electrónico es requerido",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ingrese un correo electrónico válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Correo electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan@example.com" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "El teléfono es requerido",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Ingrese un número de teléfono válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Teléfono de contacto</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+598 9 123 456" className={inputStyles} {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/80">
                Incluya el código de país si llama desde el exterior.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Dirección <span className="text-muted-foreground font-normal text-xs">(Opcional)</span></FormLabel>
            <FormControl>
              <Input placeholder="Calle 123, Montevideo" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="experience" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Experiencia previa en forestación <span className="text-muted-foreground font-normal text-xs">(Opcional)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Describa brevemente su experiencia..." className={`min-h-[120px] resize-y ${inputStyles}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-3">
          <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <div className="space-y-4 pt-2">
        <Button type="submit" disabled={isSubmitting} variant="secondary" size="lg" className="w-full text-base font-bold tracking-wide transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.3)] active:translate-y-0 active:shadow-sm">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-4" />
              Enviando...
            </> : "Inscribirse Ahora"}
        </Button>

        <div className="flex items-center gap-4 py-2">
          <div className="h-px flex-1 bg-border/50" />
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">O también</span>
          <div className="h-px flex-1 bg-border/50" />
        </div>

        <Button asChild variant="outline" className="w-full border-primary/30 hover:bg-primary/5">
          <Link to="https://wa.me/59896344395" newTab>
            Contactar por WhatsApp
          </Link>
        </Button>
      </div>
    </Form>;
}
export default function InvitacionAInscribirse() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="invitación-a-inscribirse" className="relative py-24 md:py-32 overflow-hidden bg-background">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/dark_forest_bg.png)] bg-cover bg-center blur-[8px] scale-110 opacity-80" />
        <div className="absolute inset-0 bg-black/60 dark:bg-black/80" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`mx-auto max-w-6xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="rounded-[24px] bg-gradient-to-br from-primary/50 via-primary/10 to-transparent p-[1px] shadow-2xl">
            <div className="rounded-[24px] bg-background/90 backdrop-blur-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                
                <div className="lg:col-span-5 p-8 md:p-12 lg:p-16 flex flex-col justify-center relative overflow-hidden bg-primary/5">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-transparent" />
                  
                  <div className="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 text-primary mb-8">
                    <Leaf className="size-6" />
                  </div>
                  
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground tracking-tight mb-6 leading-tight">
                    ¿Listo para <br className="hidden lg:block" />
                    <span className="text-primary">Liderar?</span>
                  </h2>
                  
                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                    Únete a nuestro equipo de Supervisores Forestales. Buscamos profesionales comprometidos con la excelencia operativa y el cuidado del medio ambiente.
                  </p>
                  
                  <div className="space-y-4 mt-auto">
                    <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                      <div className="size-2 rounded-full bg-primary" />
                      Desarrollo profesional continuo
                    </div>
                    <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                      <div className="size-2 rounded-full bg-primary" />
                      Impacto ambiental positivo
                    </div>
                    <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                      <div className="size-2 rounded-full bg-primary" />
                      Liderazgo en campo
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 p-8 md:p-12 lg:p-16 bg-card/50">
                  <FormProvider formId="inscripcion_form_supervisorforestal" sectionName="invitacion-a-inscribirse" defaultValues={{
                  full_name: "",
                  email: "",
                  phone: "",
                  address: "",
                  experience: ""
                }} submitText="Inscribirse Ahora" successMessage="Su solicitud ha sido enviada con éxito. Nuestro equipo de selección revisará su perfil y se pondrá en contacto con usted a la brevedad.">
                    <FormContent />
                  </FormProvider>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}